import type { OsFamily, PluginParam, PluginView } from '@occ/contracts';
import { Puzzle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { runPlugin } from '../data/plugins';
import type { ScriptResult } from '../data/scripts';
import { runnablePlugins } from '../data/plugins';
import { ErrorNotice } from './ErrorNotice';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Notice,
  Select,
} from './ui';

const TITLE = (
  <span className="inline-flex items-center gap-2">
    <Puzzle className="size-4" />
    Ejecutar plugin
  </span>
);

/**
 * Ejecución de plugins (Módulo 6.1) — lo que lancelot pidió el 2026-08-14:
 * comandos empaquetados de antemano, con parámetros de entrada que llegan al
 * script como **variables de entorno**, no por sustitución de texto. Por eso
 * la vista previa enseña el script tal cual y una tabla de variables aparte
 * en vez de "resolver" el texto: el texto nunca cambia, lo que cambia es el
 * entorno en el que corre.
 *
 * Solo se ofrecen los plugins habilitados para el cliente de este nodo y
 * compatibles con su sistema operativo (`runnablePlugins`); `script.run` con
 * `libraryId` lo vuelve a comprobar en el backend, así que esto es
 * conveniencia de UI, no la autorización real.
 */

type Phase =
  | { kind: 'idle' }
  | { kind: 'running'; startedAt: number }
  | { kind: 'done'; result: ScriptResult; elapsedMs: number }
  | { kind: 'failed'; error: unknown };

export function PluginRunner({
  nodeId,
  alias,
  online,
  osFamily,
  clientId,
  disabledReason,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  osFamily: OsFamily | null;
  clientId: string | undefined;
  disabledReason: string | null;
}) {
  const [available, setAvailable] = useState<PluginView[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState('');
  const [args, setArgs] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [confirming, setConfirming] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setAvailable(null);
    runnablePlugins(clientId, osFamily)
      .then((plugins) => {
        if (cancelled) return;
        setAvailable(plugins);
        setLoadError(null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(cause);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, osFamily]);

  const selected = useMemo(
    () => available?.find((plugin) => plugin.id === selectedId) ?? null,
    [available, selectedId],
  );

  // Con un solo plugin disponible, elegir de una lista de uno no aporta nada:
  // se preselecciona y el operador va directo a rellenar los parámetros.
  useEffect(() => {
    const onlyOption = available?.length === 1 ? available[0] : undefined;
    if (onlyOption && !selectedId) setSelectedId(onlyOption.id);
  }, [available, selectedId]);

  // Rellena los valores por omisión al cambiar de plugin; no pisa lo que el
  // operador ya haya escrito para el mismo parámetro si vuelve a elegir el
  // mismo plugin dos veces.
  useEffect(() => {
    if (!selected) return;
    setArgs((current) => {
      const next = { ...current };
      for (const param of selected.paramsSchema) {
        if (next[param.name] === undefined) next[param.name] = param.default ?? '';
      }
      return next;
    });
  }, [selected]);

  useEffect(() => {
    if (phase.kind !== 'running') return;
    const startedAt = phase.startedAt;
    setElapsedSeconds(0);
    const timer = setInterval(
      () => setElapsedSeconds(Math.round((Date.now() - startedAt) / 1_000)),
      1_000,
    );
    return () => clearInterval(timer);
  }, [phase]);

  const missingRequired = (selected?.paramsSchema ?? []).filter(
    (param) => param.required && !(args[param.name] ?? '').trim(),
  );

  const execute = async () => {
    if (!selected) return;
    setConfirming(false);
    const startedAt = Date.now();
    setPhase({ kind: 'running', startedAt });
    try {
      const result = await runPlugin(nodeId, selected.id, args, DEFAULT_TIMEOUT_SECONDS);
      setPhase({ kind: 'done', result, elapsedMs: Date.now() - startedAt });
    } catch (error) {
      setPhase({ kind: 'failed', error });
    }
  };

  const running = phase.kind === 'running';
  const blocked = disabledReason ?? (online ? null : 'El nodo no está conectado');

  if (!clientId || available === null) {
    return (
      <Card title={TITLE}>
        {loadError ? (
          <ErrorNotice error={loadError} what="Cargar los plugins disponibles" />
        ) : (
          <Loading label="Buscando plugins disponibles para este cliente…" />
        )}
      </Card>
    );
  }

  if (available.length === 0) {
    return (
      <Card title={TITLE}>
        <EmptyState title="Sin plugins habilitados para este cliente">
          Un administrador puede habilitarlos desde Administración → Plugins.
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card
      title={TITLE}
      actions={
        <Button
          variant="primary"
          disabled={running || blocked !== null || !selected || missingRequired.length > 0}
          title={blocked ?? undefined}
          onClick={() => setConfirming(true)}
        >
          {running ? `Ejecutando… ${elapsedSeconds} s` : 'Ejecutar'}
        </Button>
      }
    >
      <div className="grid gap-3">
        {blocked && <Notice tone="warn">{blocked}</Notice>}

        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Plugin"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">Elegir…</option>
            {available.map((plugin) => (
              <option key={plugin.id} value={plugin.id}>
                {plugin.name} (v{plugin.version})
              </option>
            ))}
          </Select>
          {osFamily && <Badge>nodo {osFamily}</Badge>}
          {selected && (
            <Badge>
              {selected.steps.length} paso{selected.steps.length === 1 ? '' : 's'}
            </Badge>
          )}
        </div>

        {selected?.description && <p className="text-sm opacity-70">{selected.description}</p>}

        {selected && selected.paramsSchema.length > 0 && (
          <div className="grid gap-2 rounded bg-accent/20 p-3 sm:grid-cols-2">
            {selected.paramsSchema.map((param) => (
              <ParamField
                key={param.name}
                param={param}
                value={args[param.name] ?? ''}
                onChange={(value) => setArgs((current) => ({ ...current, [param.name]: value }))}
              />
            ))}
          </div>
        )}

        {running && (
          <Notice tone="pending" title={`Ejecutando en ${alias} desde hace ${elapsedSeconds} s`}>
            La salida llega completa al terminar, un paso a la vez.
          </Notice>
        )}

        {phase.kind === 'failed' && (
          <ErrorNotice
            error={phase.error}
            what="Ejecutar el plugin"
            pendingHint="Necesita la capacidad «exec» en el agente y el túnel abierto hasta el nodo."
          />
        )}

        {phase.kind === 'done' && (
          <PluginOutput result={phase.result} elapsedMs={phase.elapsedMs} />
        )}
      </div>

      {selected && (
        <ConfirmDialog
          open={confirming}
          title="Ejecutar un plugin en un nodo de producción"
          confirmLabel="Ejecutar ahora"
          onCancel={() => setConfirming(false)}
          onConfirm={() => void execute()}
        >
          <p>
            Se va a ejecutar <strong>{selected.name}</strong> en <strong>{alias}</strong>, en{' '}
            {selected.steps.length} paso{selected.steps.length === 1 ? '' : 's'}.
          </p>

          {selected.paramsSchema.length > 0 && (
            <div className="grid gap-1.5 rounded bg-accent/20 p-2.5">
              <span className="text-xs uppercase tracking-wide opacity-50">
                Variables de entorno
              </span>
              <table className="w-full text-xs">
                <tbody>
                  {selected.paramsSchema.map((param) => (
                    <tr key={param.name}>
                      <td className="pr-2 py-0.5 font-mono opacity-70">{param.name}</td>
                      <td className="py-0.5 font-mono">
                        {args[param.name] || <span className="opacity-40">(vacío)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-2">
            {selected.steps.map((step, index) => (
              <div key={index} className="grid gap-1">
                <span className="text-xs uppercase tracking-wide opacity-50">
                  Paso {index + 1}/{selected.steps.length} · {step.shell}
                </span>
                <pre className="max-h-32 overflow-auto rounded bg-black/5 dark:bg-white/5 p-2 text-xs leading-5">
                  {step.script}
                </pre>
              </div>
            ))}
          </div>

          <Notice tone="warn">
            Queda en la bitácora a tu nombre, con el plugin y la versión que corrió. El agente lo
            ejecuta con los privilegios del servicio, que en Windows es <code>SYSTEM</code>.
          </Notice>
        </ConfirmDialog>
      )}
    </Card>
  );
}

function ParamField({
  param,
  value,
  onChange,
}: {
  param: PluginParam;
  value: string;
  onChange: (value: string) => void;
}) {
  const label = (param.label ?? param.name) + (param.required ? ' *' : '');
  if (param.type === 'boolean') {
    return (
      <Select
        label={label}
        value={value || 'false'}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </Select>
    );
  }
  return (
    <Field
      label={label}
      type={param.type === 'number' ? 'number' : 'text'}
      value={value}
      placeholder={param.default}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function PluginOutput({ result, elapsedMs }: { result: ScriptResult; elapsedMs: number }) {
  const failed = result.exitCode !== 0;
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={failed ? 'warn' : 'ok'}>código de salida {result.exitCode}</Badge>
        <span className="opacity-60">{(elapsedMs / 1_000).toFixed(1)} s</span>
        {result.truncated && <Badge tone="warn">salida truncada por el agente</Badge>}
      </div>
      <pre className="max-h-72 overflow-auto rounded bg-black/5 dark:bg-white/5 p-3 text-xs leading-5">
        {result.stdout || '(sin salida)'}
      </pre>
      {result.stderr.trim() !== '' && (
        <pre className="max-h-72 overflow-auto rounded bg-red-500/10 p-3 text-xs leading-5 text-red-700 dark:text-red-300">
          {result.stderr}
        </pre>
      )}
    </div>
  );
}

const DEFAULT_TIMEOUT_SECONDS = 120;
